import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy } from '@saleshandy/icons';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { accessibleOnClick, toTelHref } from '../../../../../shared/utils';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import CellFeedbackButtons from '../../table/cell-renderers/cell-feedback-buttons';
import { LeadRevealFeedbackSentiment } from '../../../helpers/leads.api';

interface PhoneListWithCopyProps {
  phones: string[];
  className?: string;
  placement?: Placement;
  feedbackProps?: {
    leadId: number;
    initialSentiment?: 'positive' | 'negative';
    initialMessage?: string;
    onFeedbackSubmitted?: (
      sentiment: LeadRevealFeedbackSentiment,
      message?: string,
    ) => void;
  };
}

const PhoneListWithCopy: React.FC<PhoneListWithCopyProps> = ({
  phones,
  className = 'v2-lead-phone-cell',
  placement = Placement.Auto,
  feedbackProps,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);
  const [isFeedbackPopoverOpen, setIsFeedbackPopoverOpen] = useState(false);
  // The feedback buttons are hidden via CSS until the row is hovered, yet they
  const [hasFeedbackMounted, setHasFeedbackMounted] = useState(false);

  useEffect(() => {
    const handleScrollCapture = (e: Event) => {
      if (!showPopover) return;
      const target = e.target as HTMLElement | null;
      // Don't close when user scrolls inside the popover itself.
      if (target?.closest?.('.bs-popover-inner, .popover')) {
        return;
      }
      // Close on any scroll (table/page/modal scroll).
      setShowPopover(false);
    };
    document.addEventListener('scroll', handleScrollCapture, true);
    return () => {
      document.removeEventListener('scroll', handleScrollCapture, true);
    };
  }, [showPopover]);

  const normalized = useMemo(() => {
    const cleaned = (phones || []).filter((p) => p && String(p).trim());
    const first = cleaned[0] || '';
    return {
      phones: cleaned,
      firstPhone: first,
      remainingCount: Math.max(0, cleaned.length - 1),
    };
  }, [phones]);

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator?.clipboard?.writeText(text || '');
      setCopiedPhone(text);
      setTimeout(() => setCopiedPhone(null), 2000);
    } catch (_e) {
      // ignore
    }
  }, []);

  if (!normalized.firstPhone) {
    return <div className={className}>-</div>;
  }

  return (
    <div
      className={className}
      onMouseEnter={
        feedbackProps && !hasFeedbackMounted
          ? () => setHasFeedbackMounted(true)
          : undefined
      }
    >
      <TruncatedTextWithAutoTooltip
        className="v2-lead-phone-text"
        text={normalized.firstPhone || ''}
        telHref={toTelHref(normalized.firstPhone) ?? undefined}
        placement={Placement.Bottom}
      />
      <div
        className="v2-lead-phone-copy-icon"
        {...accessibleOnClick((e) => {
          e?.stopPropagation?.();
          handleCopy(normalized.firstPhone);
        })}
        onMouseDownCapture={(e) => e.stopPropagation()}
      >
        <OverlayTooltip
          placement={Placement.Bottom}
          text={
            copiedPhone === normalized.firstPhone
              ? 'Phone Copied'
              : 'Copy Phone'
          }
        >
          <span>
            {copiedPhone === normalized.firstPhone ? <Check /> : <Copy />}
          </span>
        </OverlayTooltip>
      </div>

      {feedbackProps && hasFeedbackMounted && (
        <div
          className={`cell-feedback-btns-wrapper${
            isFeedbackPopoverOpen ? ' cell-feedback-btns-wrapper--active' : ''
          }`}
        >
          <CellFeedbackButtons
            leadId={feedbackProps.leadId}
            initialSentiment={feedbackProps.initialSentiment}
            initialMessage={feedbackProps.initialMessage}
            onFeedbackSubmitted={feedbackProps.onFeedbackSubmitted}
            onPopoverOpenChange={setIsFeedbackPopoverOpen}
          />
        </div>
      )}

      {normalized.remainingCount > 0 && (
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          placement={placement}
          className="v2-lead-phones-popover-container"
          content={
            <div className="v2-lead-phones-popover">
              {normalized.phones.map((phone) => (
                <div key={phone} className="v2-lead-phone-item">
                  <TruncatedTextWithAutoTooltip
                    className="v2-lead-phone-text"
                    text={phone || ''}
                    telHref={toTelHref(phone) ?? undefined}
                    placement={Placement.Auto}
                  />
                  <div
                    className="v2-lead-phone-copy-icon"
                    {...accessibleOnClick((e) => {
                      e?.stopPropagation?.();
                      handleCopy(phone);
                    })}
                    onMouseDownCapture={(e) => e.stopPropagation()}
                  >
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text={
                        copiedPhone === phone ? 'Phone Copied' : 'Copy Phone'
                      }
                    >
                      <span>
                        {copiedPhone === phone ? <Check /> : <Copy />}
                      </span>
                    </OverlayTooltip>
                  </div>
                </div>
              ))}
            </div>
          }
          rootClose
        >
          <span
            className={`v2-lead-phone-count ${showPopover ? 'active' : ''}`}
          >
            +{normalized.remainingCount}
          </span>
        </OverlayPopover>
      )}
    </div>
  );
};

export default PhoneListWithCopy;
